import { useCallback } from "react";
import { convertirAMonedaLocal } from "../../../utils/calculateVentas";

export const useVPCierresCalcs = ({
  oportunidades,
  salesBTL,
  negociosCarteleria,
  contratosData,
  oportunidadesOtroPais,
  salesBTLOtroPais,
  negociosCarteleriaOtroPais,
  contratosDataOtroPais,
  pais,
}) => {
  // Cierres Publicidad genérico
  const calcCierresPub = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((o) => o.estatus === "100%" && !o.eliminada && o.mes_cierre)
      .forEach((o) => {
        const [mesStr, anoStr] = o.mes_cierre.split("-");
        if (Number(anoStr) === ano) {
          const importe = parseFloat(o.importe) || 0;
          const mesIdx = Number(mesStr) - 1;
          const ultimoDia = new Date(ano, mesIdx + 1, 0);
          const fechaConversion = ultimoDia.toISOString().split("T")[0];
          const valorLocal = o.moneda_dolar
            ? convertirAMonedaLocal(importe, "USD", paisParam, fechaConversion)
            : importe;
          valores[mesIdx] += valorLocal;
        }
      });
    return valores;
  }, []);

  // Cierres BTL genérico
  const calcCierresBTL = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((b) => b.status === 100 && !b.eliminada && b.mes_cierre)
      .forEach((b) => {
        const [mesStr, anoStr] = b.mes_cierre.split("-");
        if (Number(anoStr) === ano) {
          const importe = Number(b.total_investment) || 0;
          const mesIdx = Number(mesStr) - 1;
          const ultimoDia = new Date(ano, mesIdx + 1, 0);
          const fechaConversion = ultimoDia.toISOString().split("T")[0];
          const valorLocal = b.moneda_dolar
            ? convertirAMonedaLocal(importe, "USD", paisParam, fechaConversion)
            : importe;
          valores[mesIdx] += valorLocal;
        }
      });
    return valores;
  }, []);

  // Cierres Hardware genérico (100% y Activo = ya cerrados)
  const calcCierresHW = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((n) => n.incluye_hardware && !n.eliminada && ["100%", "Activo"].includes(n.estado) && n.fecha_cierre)
      .forEach((n) => {
        const fecha = new Date(n.fecha_cierre + "T00:00:00");
        if (fecha.getFullYear() === ano) {
          const defaultMoneda = paisParam === "Chile" ? "UF" : "PEN";
          const moneda = (n.moneda_hardware || defaultMoneda).toUpperCase();
          const ultimoDia = new Date(ano, fecha.getMonth() + 1, 0);
          const fechaConversion = ultimoDia.toISOString().split("T")[0];
          const montoHW = Number(n.monto_hardware) || 0;
          valores[fecha.getMonth()] += convertirAMonedaLocal(montoHW, moneda, paisParam, fechaConversion);
        }
      });
    return valores;
  }, []);

  // Cierres Cartelería genérico (necesita país para conversión)
  const calcCierresCart = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((n) => !n.eliminada && ["100%", "Activo"].includes(n.estado))
      .forEach((n) => {
        const defaultMoneda = paisParam === "Chile" ? "UF" : "PEN";

        // Cierre del contrato base (gestión + contenidos si no tiene rango separado)
        if (n.fecha_cierre) {
          const fecha = new Date(n.fecha_cierre + "T00:00:00");
          if (fecha.getFullYear() === ano) {
            const pantallas = parseInt(n.cantidad_pantallas) || 1;
            const plazo = parseInt(n.plazo_contrato_meses) || 0;
            const gestion = parseFloat(n.fee_gestion_unitario) || 0;
            const contenidos =
              n.incluye_contenidos && !n.contenidos_fecha_inicio ? parseFloat(n.fee_contenidos_unitario) || 0 : 0;

            const monedaGestion = (n.moneda_gestion || defaultMoneda).toUpperCase();
            const monedaContenidos = (n.moneda_contenidos || defaultMoneda).toUpperCase();
            const ultimoDiaMes = new Date(ano, fecha.getMonth() + 1, 0);
            const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

            const gestionLocal = convertirAMonedaLocal(gestion * pantallas, monedaGestion, paisParam, fechaConversion);
            const contenidosLocal = convertirAMonedaLocal(
              contenidos * pantallas,
              monedaContenidos,
              paisParam,
              fechaConversion
            );
            const montoTotal = (gestionLocal + contenidosLocal) * plazo;

            valores[fecha.getMonth()] += montoTotal;
          }
        }

        // Cierre adicional de contenidos con fecha de inicio propia
        if (n.incluye_contenidos && n.contenidos_fecha_inicio) {
          const fechaCont = new Date(n.contenidos_fecha_inicio + "T00:00:00");
          if (fechaCont.getFullYear() === ano) {
            const pantallas = parseInt(n.cantidad_pantallas) || 1;
            const contenidos = parseFloat(n.fee_contenidos_unitario) || 0;
            const monedaContenidos = (n.moneda_contenidos || defaultMoneda).toUpperCase();
            const ultimoDiaMes = new Date(ano, fechaCont.getMonth() + 1, 0);
            const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

            // Plazo contenidos = meses entre inicio y fin
            const fechaFin = n.contenidos_fecha_fin ? new Date(n.contenidos_fecha_fin + "T00:00:00") : null;
            const plazoCont = fechaFin
              ? (fechaFin.getFullYear() - fechaCont.getFullYear()) * 12 + fechaFin.getMonth() - fechaCont.getMonth() + 1
              : parseInt(n.plazo_contrato_meses) || 0;

            const contenidosLocal = convertirAMonedaLocal(
              contenidos * pantallas,
              monedaContenidos,
              paisParam,
              fechaConversion
            );
            valores[fechaCont.getMonth()] += contenidosLocal * plazoCont;
          }
        }
      });
    return valores;
  }, []);

  // Cierres Contratos genérico (suma importe_total por mes_cierre)
  const calcCierresContratos = useCallback((data, paisParam, ano) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => c.estatus === "100%" && !c.eliminada && c.mes_cierre)
      .forEach((c) => {
        const [mesStr, anoStr] = c.mes_cierre.split("-");
        if (Number(anoStr) === ano) {
          const importe = Number(c.importe_total) || 0;
          const mesIdx = Number(mesStr) - 1;
          const ultimoDia = new Date(ano, mesIdx + 1, 0);
          const fechaConversion = ultimoDia.toISOString().split("T")[0];
          const valorLocal = c.moneda_dolar
            ? convertirAMonedaLocal(importe, "USD", paisParam, fechaConversion)
            : importe;
          valores[mesIdx] += valorLocal;
        }
      });
    return valores;
  }, []);

  // Funciones expuestas (país actual)
  const calcularCierresPub = useCallback(
    (ano) => calcCierresPub(oportunidades, pais, ano),
    [oportunidades, pais, calcCierresPub]
  );
  const calcularCierresBTL = useCallback(
    (ano) => calcCierresBTL(salesBTL, pais, ano),
    [salesBTL, pais, calcCierresBTL]
  );
  const calcularCierresHW = useCallback(
    (ano) => calcCierresHW(negociosCarteleria, pais, ano),
    [negociosCarteleria, pais, calcCierresHW]
  );
  const calcularCierresCart = useCallback(
    (ano) => calcCierresCart(negociosCarteleria, pais, ano),
    [negociosCarteleria, pais, calcCierresCart]
  );

  const calcularCierresContratos = useCallback(
    (ano) => calcCierresContratos(contratosData, pais, ano),
    [contratosData, pais, calcCierresContratos]
  );

  // Funciones expuestas (otro país)
  const otroPais = pais === "Chile" ? "Perú" : "Chile";
  const calcularCierresPubOtro = useCallback(
    (ano) => calcCierresPub(oportunidadesOtroPais, otroPais, ano),
    [oportunidadesOtroPais, otroPais, calcCierresPub]
  );
  const calcularCierresBTLOtro = useCallback(
    (ano) => calcCierresBTL(salesBTLOtroPais, otroPais, ano),
    [salesBTLOtroPais, otroPais, calcCierresBTL]
  );
  const calcularCierresHWOtro = useCallback(
    (ano) => calcCierresHW(negociosCarteleriaOtroPais, otroPais, ano),
    [negociosCarteleriaOtroPais, otroPais, calcCierresHW]
  );
  const calcularCierresCartOtro = useCallback(
    (ano) => calcCierresCart(negociosCarteleriaOtroPais, otroPais, ano),
    [negociosCarteleriaOtroPais, otroPais, calcCierresCart]
  );

  const calcularCierresContratosOtro = useCallback(
    (ano) => calcCierresContratos(contratosDataOtroPais, otroPais, ano),
    [contratosDataOtroPais, otroPais, calcCierresContratos]
  );

  return {
    calcularCierresPub,
    calcularCierresBTL,
    calcularCierresHW,
    calcularCierresCart,
    calcularCierresContratos,
    calcularCierresPubOtro,
    calcularCierresBTLOtro,
    calcularCierresHWOtro,
    calcularCierresCartOtro,
    calcularCierresContratosOtro,
  };
};
